(() => {
  const accordionControllers = new WeakMap();

  const initializeThumbnailRail = (root = document) => {
    root.querySelectorAll('.mountra-product-page media-gallery').forEach((gallery) => {
      if (gallery.dataset.mountraRailInitialized === 'true') return;
      gallery.dataset.mountraRailInitialized = 'true';

      const thumbnailList = gallery.querySelector('.thumbnail-list');
      if (!thumbnailList) return;

      const keepActiveThumbnailVisible = () => {
        if (!window.matchMedia('(min-width: 750px)').matches) return;
        thumbnailList.querySelector('[aria-current="true"]')?.closest('li')?.scrollIntoView({ block: 'nearest' });
      };

      const observer = new MutationObserver((mutations) => {
        if (mutations.some(({ attributeName }) => attributeName === 'aria-current')) {
          keepActiveThumbnailVisible();
        }
      });

      observer.observe(thumbnailList, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-current'],
      });
    });
  };

  const removeDuplicateSizeGuides = () => {
    const seen = new Map();
    document.querySelectorAll('body > modal-dialog[id^="MountraSizeGuide-"]').forEach((modal) => {
      const previous = seen.get(modal.id);
      if (previous) previous.remove();
      seen.set(modal.id, modal);
    });
  };

  const initializeProductAccordions = (root = document) => {
    root.querySelectorAll('.mountra-product-page .product__info-container').forEach((productInfo) => {
      // Only direct product-information rows belong to this accordion group.
      // This deliberately excludes complementary-product accordions and app internals.
      const accordions = Array.from(productInfo.querySelectorAll(':scope > .product__accordion > details'));
      if (!accordions.length) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const canAnimate = typeof Element.prototype.animate === 'function' && !reduceMotion;

      accordions.forEach((details) => {
        const summary = details.querySelector('summary');
        const content = details.querySelector('.accordion__content');
        if (!summary || !content) return;

        if (details.dataset.mountraAccordionInitialized === 'true') return;
        details.dataset.mountraAccordionInitialized = 'true';

        if (!canAnimate) {
          details.addEventListener('toggle', () => {
            if (!details.open) return;
            accordions.forEach((sibling) => {
              if (sibling !== details && sibling.open) sibling.open = false;
            });
          });
          return;
        }

        details.removeAttribute('name');
        summary.removeAttribute('role');
        summary.removeAttribute('aria-expanded');

        const state = {
          animation: null,
          desiredOpen: details.open,
          generation: 0,
        };

        const setOpen = (shouldOpen) => {
          if (state.desiredOpen === shouldOpen && !state.animation) return;

          state.generation += 1;
          const generation = state.generation;
          const currentHeight = details.getBoundingClientRect().height;

          state.animation?.cancel();
          state.animation = null;
          state.desiredOpen = shouldOpen;

          details.style.height = `${currentHeight}px`;
          details.style.overflow = 'hidden';

          if (shouldOpen && !details.open) details.open = true;

          const targetHeight = shouldOpen ? summary.offsetHeight + content.offsetHeight : summary.offsetHeight;
          const heightDifference = Math.abs(currentHeight - targetHeight);

          if (heightDifference < 1) {
            if (!shouldOpen) details.open = false;
            details.style.removeProperty('height');
            details.style.removeProperty('overflow');
            return;
          }

          state.animation = details.animate(
            [{ height: `${currentHeight}px` }, { height: `${targetHeight}px` }],
            {
              duration: 260,
              easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            }
          );

          state.animation.onfinish = () => {
            if (generation !== state.generation) return;
            if (!shouldOpen) details.open = false;
            details.style.removeProperty('height');
            details.style.removeProperty('overflow');
            state.animation = null;
          };

          state.animation.oncancel = () => {
            if (generation === state.generation) state.animation = null;
          };
        };

        const controller = {
          open: () => {
            accordions.forEach((sibling) => {
              if (sibling === details) return;
              const siblingController = accordionControllers.get(sibling);
              if (siblingController) siblingController.close();
              else sibling.open = false;
            });
            setOpen(true);
          },
          close: () => setOpen(false),
          toggle: () => {
            if (state.desiredOpen) setOpen(false);
            else controller.open();
          },
        };

        accordionControllers.set(details, controller);

        summary.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            controller.toggle();
          },
          { capture: true }
        );
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initializeThumbnailRail();
    initializeProductAccordions();
  });
  document.addEventListener('shopify:section:load', (event) => {
    initializeThumbnailRail(event.target);
    initializeProductAccordions(event.target);
  });
  document.addEventListener('product-info:loaded', (event) => {
    initializeThumbnailRail(event.target);
    initializeProductAccordions(event.target);
    window.setTimeout(removeDuplicateSizeGuides, 0);
  });

  if (!window.mountraProductAccordionEditorListenerBound) {
    window.mountraProductAccordionEditorListenerBound = true;
    document.addEventListener('shopify:block:select', (event) => {
      const accordion = event.target.matches('.product__accordion')
        ? event.target
        : event.target.closest('.product__accordion');
      const isDirectProductAccordion = accordion?.parentElement?.matches('.product__info-container');
      const details = isDirectProductAccordion ? accordion.querySelector(':scope > details') : null;

      if (!details) return;
      const controller = accordionControllers.get(details);
      if (controller) controller.open();
      else details.open = true;
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { sectionId, html } }) => {
      const liveButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
      const nextButton = html.getElementById(`ProductSubmitButton-${sectionId}`);
      const liveLabel = liveButton?.querySelector('.mountra-product-form__label');
      const nextLabel = nextButton?.querySelector('.mountra-product-form__label');

      if (liveLabel && nextLabel) liveLabel.innerHTML = nextLabel.innerHTML;
      initializeThumbnailRail(document);
    });
  }
})();
